"use client";

import { useEffect, useState } from "react";

export type TerminalLine =
  | { kind: "cmd";    text: string }
  | { kind: "output"; text: string; color?: string };

const AZURE_SCRIPT: TerminalLine[] = [
  { kind: "cmd",    text: "az account list --query '[].{name:name, id:id}'" },
  { kind: "output", text: '[{"name": "prod-subscription", "id": "8f2a..."}]' },
  { kind: "cmd",    text: "az role assignment list --assignee managed-identity-id" },
  { kind: "output", text: "Found: Contributor on /subscriptions/8f2a...", color: "text-cb-success" },
  { kind: "cmd",    text: "az keyvault secret list --vault-name prod-kv" },
  { kind: "output", text: "db-password  api-key  tls-cert", color: "text-cb-warning" },
  { kind: "cmd",    text: "az keyvault secret show --name db-password --vault-name prod-kv" },
  { kind: "output", text: '{"value": "P@ssw0rd_Found!"}', color: "text-cb-danger" },
];

const TYPE_SPEED   = 38;  // ms per character
const CMD_PAUSE    = 200; // ms after command finishes before output appears
const OUTPUT_PAUSE = 280; // ms after output before next command starts

interface AnimatedTerminalProps {
  script?: TerminalLine[];
  title?: string;
}

export function AnimatedTerminal({ script = AZURE_SCRIPT, title = "cb-session, BreachingAzure" }: AnimatedTerminalProps) {
  // Lines fully committed to the display
  const [done, setDone] = useState<TerminalLine[]>([]);
  // Index into script we're currently animating
  const [idx, setIdx] = useState(0);
  // Characters typed so far for the current command line
  const [chars, setChars] = useState(0);
  const [cursorOn, setCursorOn] = useState(true);

  // Cursor blink
  useEffect(() => {
    const t = setInterval(() => setCursorOn((v) => !v), 530);
    return () => clearInterval(t);
  }, []);

  // Animation driver
  useEffect(() => {
    if (idx >= script.length) return;
    const line = script[idx];

    if (line.kind === "cmd") {
      if (chars < line.text.length) {
        const t = setTimeout(() => setChars((c) => c + 1), TYPE_SPEED);
        return () => clearTimeout(t);
      }
      // Command fully typed, pause then commit and move on
      const t = setTimeout(() => {
        setDone((d) => [...d, line]);
        setIdx((i) => i + 1);
        setChars(0);
      }, CMD_PAUSE);
      return () => clearTimeout(t);
    }

    // Output line, appear after a short pause then advance
    const t = setTimeout(() => {
      setDone((d) => [...d, line]);
      setIdx((i) => i + 1);
      setChars(0);
    }, OUTPUT_PAUSE);
    return () => clearTimeout(t);
  }, [idx, chars, script]);

  const current = idx < script.length ? script[idx] : null;
  const finished = idx >= script.length;

  return (
    <div className="rounded-xl border border-cb-border bg-cb-surface shadow-2xl shadow-black/50 overflow-hidden">
      {/* Window chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-cb-border bg-cb-surface-2">
        <div className="h-3 w-3 rounded-full bg-cb-danger/60" />
        <div className="h-3 w-3 rounded-full bg-cb-warning/60" />
        <div className="h-3 w-3 rounded-full bg-cb-success/60" />
        <span className="ml-2 text-xs text-cb-muted-2 font-mono">{title}</span>
      </div>

      {/* Terminal body — min-height sized for the script on desktop; on mobile text wraps naturally */}
      <div
        className="p-5 font-mono text-xs space-y-2 leading-relaxed overflow-hidden"
        style={{ minHeight: script.length * 28 + 40 }}
      >
        {/* Committed lines */}
        {done.map((line, i) =>
          line.kind === "cmd" ? (
            <p key={i}>
              <span className="text-cb-blue">$ </span>
              <span className="text-cb-text">{line.text}</span>
            </p>
          ) : (
            <p key={i} className={line.color ?? "text-cb-muted"}>{line.text}</p>
          )
        )}

        {/* Currently typing command */}
        {current?.kind === "cmd" && (
          <p>
            <span className="text-cb-blue">$ </span>
            <span className="text-cb-text">{current.text.slice(0, chars)}</span>
            <span
              className="inline-block w-[6px] h-[0.9em] bg-cb-blue align-middle ml-px"
              style={{ opacity: cursorOn ? 1 : 0 }}
            />
          </p>
        )}

        {/* Idle cursor after animation completes */}
        {finished && (
          <span
            className="inline-block w-[6px] h-[0.9em] bg-cb-blue align-middle"
            style={{ opacity: cursorOn ? 1 : 0 }}
          />
        )}
      </div>
    </div>
  );
}
